import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';
import { apiDelete, apiGet, apiPost, apiPut, apiUpload } from './api';
import { VIRGIN_CATEGORY_FILTER } from '../utils/materialTypeFilters';

const MASTER_TABS = [
    { key: 'hsn', label: 'HSN' },
    { key: 'importers', label: 'Importers' },
    { key: 'suppliers', label: 'Suppliers' },
    { key: 'exporters', label: 'Exporters' },
    { key: 'buyers', label: 'Buyers' },
    { key: 'indianPorts', label: 'Indian Ports' },
    { key: 'shipmentPorts', label: 'Shipment Ports' },
    { key: 'units', label: 'Units' },
    { key: 'countries', label: 'Countries' },
];

const emptyFormFor = (master) => {
    if (master === 'hsn') return { hsn_code: '', description: '', category_id: '', active: true, needs_review: false };
    if (master === 'countries') return { country_name: '', iso_code: '', iso3_code: '', needs_review: false, active: true };
    return { name: '', active: true };
};

const ImportMasters = () => {
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [categories, setCategories] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [form, setForm] = useState(emptyFormFor('hsn'));
    const [hsnFile, setHsnFile] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const perPage = 25;
    const master = MASTER_TABS[activeTab].key;

    const isHsn = master === 'hsn';
    const isCountry = master === 'countries';

    const sortField = useMemo(() => {
        if (isHsn) return 'hsn_code';
        if (isCountry) return 'country_name';
        return 'name';
    }, [isHsn, isCountry]);

    const fetchRows = async () => {
        setError('');
        try {
            const response = await apiGet(`/import/masters/${master}`, {
                _start: page * perPage,
                _end: page * perPage + perPage,
                _sort: sortField,
                _order: 'ASC',
                q: search,
            });
            setRows(response.json || []);
            setTotal(response.total);
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await apiGet('/categories', {
                _start: 0,
                _end: 500,
                _sort: 'category_name',
                _order: 'ASC',
                ...VIRGIN_CATEGORY_FILTER,
            });
            setCategories(response.json || []);
        } catch (err) {
            setCategories([]);
        }
    };

    useEffect(() => {
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [master, page]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleTabChange = (event, value) => {
        setActiveTab(value);
        setSearch('');
        setPage(0);
        setRows([]);
        setTotal(0);
        setMessage('');
        setError('');
    };

    const openCreate = () => {
        setEditingRecord(null);
        setForm(emptyFormFor(master));
        setDialogOpen(true);
    };

    const openEdit = (record) => {
        setEditingRecord(record);
        setForm({
            ...emptyFormFor(master),
            ...record,
            category_id: record.category_id || '',
        });
        setDialogOpen(true);
    };

    const saveRecord = async () => {
        setError('');
        try {
            const payload = { ...form };
            if (isHsn) {
                payload.category_id = payload.category_id ? Number(payload.category_id) : null;
            }
            const path = `/import/masters/${master}${editingRecord ? `/${editingRecord.id}` : ''}`;
            if (editingRecord) {
                await apiPut(path, payload);
            } else {
                await apiPost(path, payload);
            }
            setDialogOpen(false);
            setMessage('Master saved.');
            fetchRows();
        } catch (err) {
            setError(err.message);
        }
    };

    const deactivateRecord = async (record) => {
        setError('');
        try {
            await apiDelete(`/import/masters/${master}/${record.id}`);
            setMessage('Master deactivated.');
            fetchRows();
        } catch (err) {
            setError(err.message);
        }
    };

    const uploadHsn = async () => {
        if (!hsnFile) {
            setError('Choose an HSN Excel file first.');
            return;
        }
        setError('');
        setMessage('');
        try {
            const formData = new FormData();
            formData.append('file', hsnFile);
            const response = await apiUpload('/import/masters/hsn/upload', formData);
            setMessage(`HSN upload complete. ${response.json.insertedOrUpdated} saved, ${response.json.failed} failed.`);
            setHsnFile(null);
            fetchRows();
        } catch (err) {
            setError(err.message);
        }
    };

    const renderDialogFields = () => {
        if (isHsn) {
            return (
                <>
                    <TextField
                        label="HSN Code"
                        value={form.hsn_code || ''}
                        onChange={(event) => setForm({ ...form, hsn_code: event.target.value })}
                        fullWidth
                        margin="dense"
                    />
                    <TextField
                        label="Description"
                        value={form.description || ''}
                        onChange={(event) => setForm({ ...form, description: event.target.value })}
                        fullWidth
                        margin="dense"
                        multiline
                        minRows={2}
                    />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Category</InputLabel>
                        <Select
                            label="Category"
                            value={form.category_id || ''}
                            onChange={(event) => setForm({ ...form, category_id: event.target.value })}
                        >
                            <MenuItem value="">Unmapped</MenuItem>
                            {categories.map((category) => (
                                <MenuItem key={category.id} value={category.id}>
                                    {category.category_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={Boolean(form.active)}
                                onChange={(event) => setForm({ ...form, active: event.target.checked })}
                            />
                        )}
                        label="Active"
                    />
                </>
            );
        }

        if (isCountry) {
            return (
                <>
                    <TextField
                        label="Country Name"
                        value={form.country_name || ''}
                        onChange={(event) => setForm({ ...form, country_name: event.target.value })}
                        fullWidth
                        margin="dense"
                    />
                    <Stack direction="row" spacing={1}>
                        <TextField
                            label="ISO2"
                            value={form.iso_code || ''}
                            onChange={(event) => setForm({ ...form, iso_code: event.target.value.toUpperCase() })}
                            margin="dense"
                        />
                        <TextField
                            label="ISO3"
                            value={form.iso3_code || ''}
                            onChange={(event) => setForm({ ...form, iso3_code: event.target.value.toUpperCase() })}
                            margin="dense"
                        />
                    </Stack>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={Boolean(form.needs_review)}
                                onChange={(event) => setForm({ ...form, needs_review: event.target.checked })}
                            />
                        )}
                        label="Needs review"
                    />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={Boolean(form.active)}
                                onChange={(event) => setForm({ ...form, active: event.target.checked })}
                            />
                        )}
                        label="Active"
                    />
                </>
            );
        }

        return (
            <>
                <TextField
                    label="Name"
                    value={form.name || ''}
                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                    fullWidth
                    margin="dense"
                />
                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={Boolean(form.active)}
                            onChange={(event) => setForm({ ...form, active: event.target.checked })}
                        />
                    )}
                    label="Active"
                />
            </>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/import')}>
                        Dashboard
                    </Button>
                    <Typography variant="h6">Import/Export Masters</Typography>
                </Stack>
                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    Add
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ mb: 2 }}>
                <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                    {MASTER_TABS.map((tab) => <Tab key={tab.key} label={tab.label} />)}
                </Tabs>
            </Paper>

            {isHsn && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                        <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />}>
                            Choose HSN Excel
                            <input
                                hidden
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={(event) => setHsnFile(event.target.files?.[0] || null)}
                            />
                        </Button>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                            {hsnFile ? hsnFile.name : 'Upload HSN_MSTR with optional CATEGORY_ID or CATEGORY_NAME.'}
                        </Typography>
                        <Button variant="contained" onClick={uploadHsn} disabled={!hsnFile}>
                            Upload HSN
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                        size="small"
                        label={isHsn ? 'Search HSN or description' : 'Search'}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        sx={{ minWidth: 280 }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<SearchIcon />}
                        onClick={() => {
                            setPage(0);
                            fetchRows();
                        }}
                    >
                        Search
                    </Button>
                </Stack>
            </Paper>

            {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <TableContainer component={Paper} variant="outlined">
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            {isHsn ? (
                                <>
                                    <TableCell>HSN</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Category</TableCell>
                                </>
                            ) : isCountry ? (
                                <>
                                    <TableCell>Country</TableCell>
                                    <TableCell>ISO2</TableCell>
                                    <TableCell>ISO3</TableCell>
                                    <TableCell>Review</TableCell>
                                </>
                            ) : (
                                <TableCell>Name</TableCell>
                            )}
                            <TableCell>Active</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>{row.id}</TableCell>
                                {isHsn ? (
                                    <>
                                        <TableCell>{row.hsn_code}</TableCell>
                                        <TableCell>{row.description}</TableCell>
                                        <TableCell>{row.category_name || 'Unmapped'}</TableCell>
                                    </>
                                ) : isCountry ? (
                                    <>
                                        <TableCell>{row.country_name}</TableCell>
                                        <TableCell>{row.iso_code}</TableCell>
                                        <TableCell>{row.iso3_code}</TableCell>
                                        <TableCell>{row.needs_review ? 'Yes' : 'No'}</TableCell>
                                    </>
                                ) : (
                                    <TableCell>{row.name}</TableCell>
                                )}
                                <TableCell>{row.active ? 'Yes' : 'No'}</TableCell>
                                <TableCell align="right">
                                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEdit(row)}>
                                        Edit
                                    </Button>
                                    {row.active && (
                                        <Button size="small" color="warning" onClick={() => deactivateRecord(row)}>
                                            Deactivate
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6}>No records</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
                <Typography sx={{ alignSelf: 'center' }}>Page {page + 1}</Typography>
                <Button disabled={(page + 1) * perPage >= total} onClick={() => setPage(page + 1)}>Next</Button>
            </Stack>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editingRecord ? 'Edit Master' : 'Add Master'}</DialogTitle>
                <DialogContent>
                    {renderDialogFields()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveRecord}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ImportMasters;
